/**
 * Crypto Buffer API Service
 * Handles /admin/crypto buffer configuration endpoints
 */

import axiosInstance from '@/lib/axios'
import API_ROUTES from '@/lib/api-routes'
import type {
    ApiEnvelope,
    CreateBufferTierRequest,
    CryptoBufferConfig,
    CryptoSummary,
    PatchCryptoBufferRequest,
} from '@/types/crypto-buffer'

/**
 * List cryptocurrencies
 * GET /admin/crypto
 */
export const getCryptocurrencies = async (): Promise<CryptoSummary[]> => {
    const response = await axiosInstance.get<ApiEnvelope<CryptoSummary[]>>(
        API_ROUTES.CRYPTO_BUFFER.LIST
    )
    return response.data.data
}

/**
 * Buffer configuration (defaults + tiers) for one crypto
 * GET /admin/crypto/{cryptoId}
 */
export const getCryptoBufferConfig = async (
    cryptoId: string
): Promise<CryptoBufferConfig> => {
    const response = await axiosInstance.get<ApiEnvelope<CryptoBufferConfig>>(
        API_ROUTES.CRYPTO_BUFFER.DETAIL(cryptoId)
    )
    return response.data.data
}

/**
 * Update default/max buffer and existing tiers
 * PATCH /admin/crypto/{cryptoId}
 */
export const patchCryptoBuffer = async (
    cryptoId: string,
    data: PatchCryptoBufferRequest
): Promise<void> => {
    await axiosInstance.patch(API_ROUTES.CRYPTO_BUFFER.PATCH(cryptoId), data)
}

/**
 * Create a buffer tier
 * POST /admin/crypto/{cryptoId}/buffer-tiers
 */
export const createBufferTier = async (
    cryptoId: string,
    data: CreateBufferTierRequest
): Promise<void> => {
    await axiosInstance.post(
        API_ROUTES.CRYPTO_BUFFER.CREATE_TIER(cryptoId),
        data
    )
}

/**
 * Delete a buffer tier
 * DELETE /admin/crypto/{cryptoId}/buffer-tiers/{tierId}
 */
export const deleteBufferTier = async (
    cryptoId: string,
    tierId: string
): Promise<void> => {
    await axiosInstance.delete(
        API_ROUTES.CRYPTO_BUFFER.DELETE_TIER(cryptoId, tierId)
    )
}
